import { useMemo } from 'react';

// Chart color schemes for light and dark modes
export const chartColors = {
  light: {
    primary: '#2196f3',
    secondary: '#4caf50',
    tertiary: '#ff9800',
    quaternary: '#9c27b0',
    quinary: '#f44336',
    senary: '#00bcd4',
    septenary: '#795548'
  },
  dark: {
    primary: '#90caf9',
    secondary: '#81c784',
    tertiary: '#ffb74d',
    quaternary: '#ba68c8',
    quinary: '#e57373',
    senary: '#4dd0e1',
    septenary: '#a1887f'
  }
};

// Common chart options for different chart types
export const createChartOptions = (mode, options = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
        font: { size: 12 }
      }
    },
    tooltip: {
      backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)',
      titleColor: mode === 'dark' ? '#fff' : '#000',
      bodyColor: mode === 'dark' ? '#fff' : '#000',
      borderColor: mode === 'dark' ? '#333' : '#ddd',
      borderWidth: 1
    }
  },
  scales: {
    x: {
      ticks: {
        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'
      },
      grid: {
        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
      }
    },
    y: {
      ticks: {
        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'
      },
      grid: {
        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
      }
    }
  },
  ...options
});

// Radar chart specific options
export const createRadarOptions = (mode) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)'
      }
    }
  },
  scales: {
    r: {
      angleLines: {
        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
      },
      grid: {
        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
      },
      pointLabels: {
        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'
      },
      ticks: {
        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
        backdropColor: 'transparent'
      }
    }
  }
});

// Performance trend data processor
export const createPerformanceTrendData = (improvementTrend, mode) => {
  if (!improvementTrend) return null;
  
  const colors = chartColors[mode];
  
  return {
    labels: improvementTrend.map(day => 
      new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    ),
    datasets: [
      {
        label: 'Average Score',
        data: improvementTrend.map(day => day.avgScore),
        borderColor: colors.primary,
        backgroundColor: `${colors.primary}1a`,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: colors.primary,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4
      },
      {
        label: 'Average Accuracy',
        data: improvementTrend.map(day => day.avgAccuracy),
        borderColor: colors.secondary,
        backgroundColor: `${colors.secondary}1a`,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: colors.secondary,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4
      }
    ]
  };
};

// Subject performance data processor
export const createSubjectPerformanceData = (subjectPerformance, mode) => {
  if (!subjectPerformance) return null;
  
  const colors = [
    '#ff6384', '#36a2eb', '#cc65fe', '#ffce56', 
    '#4bc0c0', '#9966ff', '#ff9f40'
  ];
  
  return {
    labels: subjectPerformance.map(subject => subject.name),
    datasets: [
      {
        label: 'Accuracy (%)',
        data: subjectPerformance.map(subject => subject.accuracy),
        backgroundColor: colors,
        borderColor: mode === 'light' ? '#fff' : '#333',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      }
    ]
  };
};

// Score distribution data processor
export const createScoreDistributionData = (overview, mode) => {
  if (!overview) return null;
  
  const { testsAbove90 = 0, testsAbove80 = 0, testsAbove70 = 0, totalTests = 0 } = overview;
  const testsBelow70 = totalTests - testsAbove70;
  
  return {
    labels: ['90+ Score', '80-89 Score', '70-79 Score', 'Below 70'],
    datasets: [
      {
        data: [testsAbove90, testsAbove80 - testsAbove90, testsAbove70 - testsAbove80, testsBelow70],
        backgroundColor: [
          '#4caf50',
          '#8bc34a',
          '#ff9800',
          '#f44336'
        ],
        borderColor: mode === 'light' ? '#fff' : '#333',
        borderWidth: 2,
        hoverOffset: 4
      }
    ]
  };
};

// Radar data processor
export const createRadarData = (overview, mode) => {
  if (!overview) return null;
  
  const colors = chartColors[mode];
  
  return {
    labels: ['Accuracy', 'Speed', 'Time Efficiency', 'Consistency', 'Progress'],
    datasets: [
      {
        label: 'Performance Metrics',
        data: [
          overview?.averageAccuracy || 0,
          (overview?.averageSpeed || 0) * 10, // Scale speed to 0-100
          overview?.averageTimeEfficiency || 0,
          overview?.totalTests > 0 ? (overview.testsAbove70 / overview.totalTests) * 100 : 0,
          (overview?.testsPerDay || 0) * 10 // Scale to 0-100
        ],
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: colors.primary,
        borderWidth: 2,
        pointBackgroundColor: colors.primary,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: colors.primary
      }
    ]
  };
};

// Hook for creating chart data with memoization
export const useChartData = (data, mode, chartType) => {
  return useMemo(() => {
    switch (chartType) {
      case 'performanceTrend':
        return createPerformanceTrendData(data?.improvementTrend, mode);
      case 'subjectPerformance':
        return createSubjectPerformanceData(data?.subjectPerformance, mode);
      case 'scoreDistribution':
        return createScoreDistributionData(data?.overview, mode);
      case 'radar':
        return createRadarData(data?.overview, mode);
      default:
        return null;
    }
  }, [data, mode, chartType]);
}; 